// Copyright (c) Bentley Systems
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { AppDispatch } from "../app/store";
import { ConfigManager } from "../Helpers/config-manager";
import { setErrorState } from "../Helpers/helper-functions";
import { mapDataToModels, mapResponseToDTO } from "../Helpers/map-response";
import { CatalogGeneralInfo } from "../ProjectManagement/Models/catalog-general-info";
import { Organization } from "./Models/organizations";
import { SearchOrgResponse } from "./Models/search-org-response";

/**
 * Hook encapsulating all API interactions for the SubscribeProjectForm:
 * - IMS organization search (with pagination / "load more")
 * - OData project search (debounced, with abort + pagination)
 * - Subscribe (POST) a selected project to an organization
 *
 * Keeps the component focused on rendering while this hook
 * owns fetch logic, loading flags, abort controllers, and saving state.
 */
type UseSubscribeProjectFormApiProps = {
  accessToken: string;
  dispatch: AppDispatch;
  orgId?: string;
  showToast: (toast: { status: string; message: string }) => void;
  closePanel: (refreshList: boolean) => void;
};

const ORG_PAGE_SIZE = 20;

export const useSubscribeProjectFormApi = ({
  accessToken,
  dispatch,
  orgId,
  showToast,
  closePanel,
}: UseSubscribeProjectFormApiProps) => {
  // --- Organization search state ---
  const [searchOrgString, setSearchOrgString] = useState("");
  const [isOrgLoading, setOrgLoading] = useState(false);
  const [organizationsFound, setOrganizationsFound] = useState(0);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization>();
  const orgApiOffsetRef = useRef(0);

  // --- Project search state ---
  const [projects, setProjects] = useState<CatalogGeneralInfo[]>([]);
  const [selectedProject, setSelectedProject] = useState<CatalogGeneralInfo>();
  const [searchProjectString, setSearchProjectString] = useState("");
  const [isProjectsLoading, setProjectsLoading] = useState(false);
  const [projectsFound, setProjectsFound] = useState(0);
  const [projectRawInput, setProjectRawInput] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // --- Abort controller refs ---
  const loadMoreOrgAbortControllerRef = useRef<AbortController | null>(null);
  const projectAbortControllerRef = useRef<AbortController | null>(null);
  const saveAbortControllerRef = useRef<AbortController | null>(null);

  // --- Mapping helper ---
  function mapSearchOrgResponseToOrganizations(
    response: SearchOrgResponse,
  ): Organization[] {
    return response.organizations.map((item) => ({
      Id: item.ultimateId ?? "",
      Name: item.ultimateName ?? "",
    }));
  }

  // --- Fetch IMS organizations ---
  const fetchOrganizations = useCallback(async (search: string, signal?: AbortSignal) => {
    if (search.trim().length < 4) { return; }

    setOrganizations([]);
    setSelectedOrganization(undefined);
    setOrganizationsFound(0);
    orgApiOffsetRef.current = 0;
    setOrgLoading(true);

    // Auto-append wildcard so "Bent" matches "Bentley" without
    // requiring the user to type "*" manually.  The API does not
    // allow a leading "*".
    const wildcard = search.includes("*") ? search : `${search}*`;
    const url =
      `${ConfigManager.projectProviderUrl}/api/Catalog/IMSOrganizations?searchString=${encodeURIComponent(wildcard)}&start=0&rows=${ORG_PAGE_SIZE}`;
    const runId = crypto.randomUUID();

    try {
      const orgResponse = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: accessToken,
          RunId: runId,
        },
        signal,
      });
      if (!orgResponse.ok) {
        setErrorState(orgResponse.status, dispatch, runId, true);
        return;
      }
      const orgsData = await orgResponse.json();
      const orgRespDto: SearchOrgResponse = mapResponseToDTO<
        SearchOrgResponse,
        typeof orgsData.result
      >(orgsData.result);
      const orgs = mapSearchOrgResponseToOrganizations(orgRespDto);
      setOrganizations(orgs);
      setOrganizationsFound(orgRespDto.numFound);
      orgApiOffsetRef.current = orgs.length;
    } catch (error: any) {
      if (error.name !== "AbortError") {
        setErrorState(500, dispatch, runId, true);
      }
    } finally {
      setOrgLoading(false);
    }
  }, [accessToken, dispatch]);

  // --- Fetch more organizations (pagination) ---
  const fetchMoreOrganizations = useCallback(async () => {
    loadMoreOrgAbortControllerRef.current?.abort();
    loadMoreOrgAbortControllerRef.current = new AbortController();

    const nextStart = orgApiOffsetRef.current + 1;
    // Use same wildcard wrapping as the initial search.
    const wildcard = searchOrgString.includes("*") ? searchOrgString : `${searchOrgString}*`;
    const url =
      `${ConfigManager.projectProviderUrl}/api/Catalog/IMSOrganizations` +
      `?searchString=${encodeURIComponent(wildcard)}&start=${nextStart}&rows=${ORG_PAGE_SIZE}`;
    const runId = crypto.randomUUID();
    setOrgLoading(true);

    try {
      const orgResponse = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: accessToken,
          RunId: runId,
        },
        signal: loadMoreOrgAbortControllerRef.current.signal,
      });
      if (!orgResponse.ok) {
        setErrorState(orgResponse.status, dispatch, runId, true);
        return;
      }
      const orgsData = await orgResponse.json();
      const orgRespDto = mapResponseToDTO<SearchOrgResponse, typeof orgsData.result>(orgsData.result);
      const orgs = mapSearchOrgResponseToOrganizations(orgRespDto);
      orgApiOffsetRef.current += orgs.length;
      setOrganizations((prev) => {
        const existingIds = new Set(prev.map((org) => org.Id));
        const newUnique = orgs.filter((org) => !existingIds.has(org.Id));
        return [...prev, ...newUnique];
      });
      setOrganizationsFound(orgRespDto.numFound);
    } catch (error: any) {
      if (error?.name !== "AbortError") {
        showToast({
          status: "negative",
          message: `An error occurred while fetching organizations. ActivityId="${runId}"`,
        });
      }
    } finally {
      setOrgLoading(false);
    }
  }, [searchOrgString, accessToken, showToast, dispatch]);

  // When ComboBox selects an item it auto-fills the input with the label,
  // which fires inputProps.onChange.  We skip that event so the search state
  // stays unchanged and the dropdown keeps showing the full result list.
  const justSelectedOrgRef = useRef(false);
  const justSelectedProjectRef = useRef(false);

  // --- Organization ComboBox input handler ---
  const handleOrgSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      if (justSelectedOrgRef.current) {
        justSelectedOrgRef.current = false;
        return;
      }
      setSearchOrgString(e.target.value);
    },
    [],
  );

  // --- Debounced organization search effect ---
  useEffect(() => {
    const trimmed = searchOrgString.trim();
    if (trimmed.length < 4) {
      setOrganizations([]);
      setOrganizationsFound(0);
      setOrgLoading(false);
      return;
    }

    // Show loading immediately so "No organizations found" doesn't flash
    // during the debounce delay.
    setOrgLoading(true);

    const abortController = new AbortController();
    const handler = setTimeout(() => {
      void fetchOrganizations(trimmed, abortController.signal);
    }, 400);

    return () => {
      clearTimeout(handler);
      abortController.abort();
    };
  }, [searchOrgString, fetchOrganizations]);

  // --- Fetch projects (OData) ---
  const fetchProjects = useCallback(
    // `currentLength` is passed from the call site for the append case so that
    // `projects.length` is NOT captured in the closure — preventing the
    // useCallback from being recreated on every successful fetch and causing
    // an infinite re-fetch loop.
    async (append = false, currentLength = 0) => {
      projectAbortControllerRef.current?.abort();
      projectAbortControllerRef.current = new AbortController();
      if (!append) {
        setProjects([]);
        setSelectedProject(undefined);
        setProjectsFound(0);
      }
      setProjectsLoading(true);
      const skip = append ? currentLength : 0;
      let url =
        `${ConfigManager.projectProviderUrl}/odata/Projects?$select=Id,Name,UniqueName&$skip=${skip}&$top=50&$orderby=Name asc&$count=true`;
      if (
        searchProjectString &&
        searchProjectString.trim() !== "" &&
        searchProjectString.trim() !== "*"
      ) {
        const escaped = searchProjectString.trim().replace(/'/g, "''");
        url = url.concat(
          `&$filter=contains(Name,'${encodeURIComponent(escaped)}')`,
        );
      }
      const runId = crypto.randomUUID();
      try {
        const projResp = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: runId,
          },
          signal: projectAbortControllerRef.current.signal,
        });
        if (!projResp.ok) {
          setErrorState(projResp.status, dispatch, runId, true);
          return null;
        }
        const projectsData = await projResp.json();
        const projectsDtos: CatalogGeneralInfo[] =
          mapDataToModels<CatalogGeneralInfo>(projectsData.value);
        if (append) {
          setProjects((prev) => {
            const existingIds = new Set(prev.map((p) => p.Id));
            const newUnique = projectsDtos.filter(
              (p) => !existingIds.has(p.Id),
            );
            return [...prev, ...newUnique];
          });
        } else {
          setProjects(projectsDtos);
        }
        setProjectsFound(
          projectsData["@odata.count"] ?? projectsDtos.length,
        );
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          setErrorState(500, dispatch, runId, true);
        }
      } finally {
        setProjectsLoading(false);
      }
    },
    [searchProjectString, accessToken, dispatch],
  );

  // --- Initial project load when an org becomes known ---
  // Fires on mount (if orgId is pre-supplied) and whenever the user selects
  // an organization from the ComboBox, loading the first page unfiltered.
  // Also re-fires if the user clears the search box back to empty so the
  // unfiltered list is restored. Guarded by searchProjectString so it does
  // not compete with the debounced search effect when a query is active.
  useEffect(() => {
    const hasOrg = orgId !== undefined || selectedOrganization !== undefined;
    if (hasOrg && searchProjectString.trim() === "") {
      void fetchProjects();
    }
  }, [orgId, selectedOrganization?.Id, searchProjectString, fetchProjects]);

  // --- Project ComboBox input handler ---
  const projectComboboxInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      if (justSelectedProjectRef.current) {
        justSelectedProjectRef.current = false;
        return;
      }
      const val = event.currentTarget.value;
      setProjectRawInput(val);
      setSearchProjectString(val);
    },
    [],
  );

  // --- Selection handlers ---
  const handleOrgSelect = useCallback((org: Organization | undefined) => {
    setSelectedOrganization(org);
    justSelectedOrgRef.current = true;
  }, []);

  const handleProjectSelect = useCallback((project: CatalogGeneralInfo | undefined) => {
    setSelectedProject(project);
    justSelectedProjectRef.current = true;
  }, []);

  // --- Debounced project search effect ---
  useEffect(() => {
    const trimmed = searchProjectString.trim();

    // 1-2 chars: clear results, show hint
    if (trimmed.length > 0 && trimmed.length < 3) {
      setProjects([]);
      setProjectsFound(0);
      setProjectsLoading(false);
      return;
    }

    // Empty: let the initial load effect above handle it
    if (trimmed.length === 0) {
      return;
    }

    // Show loading immediately so "No options found" doesn't flash
    // during the debounce delay.
    setProjectsLoading(true);

    const handler = setTimeout(() => {
      void fetchProjects();
    }, 300);

    return () => {
      clearTimeout(handler);
      projectAbortControllerRef.current?.abort();
    };
  }, [searchProjectString, accessToken, orgId, fetchProjects]);

  // --- Save (subscribe project to organization) ---
  const save = useCallback(async () => {
    if (!selectedProject) { return; }
    const organizationId = orgId ?? selectedOrganization?.Id;
    if (organizationId === undefined) { return; }
    saveAbortControllerRef.current?.abort();
    saveAbortControllerRef.current = new AbortController();

    setIsSaving(true);
    const saveUrl =
      `${ConfigManager.projectProviderUrl}/api/Projects/${selectedProject.UniqueName}/organizations/${organizationId}`;
    const saveRunId = crypto.randomUUID();

    try {
      const saveResponse = await fetch(saveUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: accessToken,
          RunId: saveRunId,
        },
        body: "{}",
        signal: saveAbortControllerRef.current.signal,
      });
      const saveData = await saveResponse.json();
      if (!saveData.isSuccess) {
        showToast({
          status: "negative",
          message: `${saveData.displayMessage} ActivityId="${saveRunId}"`,
        });
      } else {
        closePanel(true);
        showToast({
          status: "positive",
          message: `Project "${selectedProject.Name}" subscribed successfully!"`,
        });
      }
    } catch (error: any) {
      if (error?.name !== "AbortError") {
        showToast({
          status: "negative",
          message: `An error occurred while saving assignments. ActivityId="${saveRunId}"`,
        });
      }
    } finally {
      setIsSaving(false);
    }
  }, [selectedProject, orgId, selectedOrganization, accessToken, showToast, closePanel]);

  return {
    // Org search
    isOrgLoading,
    organizationsFound,
    organizations,
    selectedOrganization,
    handleOrgSelect,
    handleOrgSearchChange,
    fetchMoreOrganizations,
    searchOrgString,

    // Project search
    projects,
    selectedProject,
    handleProjectSelect,
    isProjectsLoading,
    projectsFound,
    projectRawInput,
    projectComboboxInputChange,
    fetchProjects,

    // Save
    isSaving,
    save,
  };
};
